import React, { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { iAxios } from '../../lib/utils';
import { Spinner } from '../common/Helper';
import { LinearMonth } from '../common/Calendar';

// Commits are read from the git_logs table (see docs/features/git-contributions.md),
// not proxied from GitLab per request — filtering, paging and aggregation all
// happen server-side.
const API = '/api/v1/contrib';
const PAGE_SIZE = 100;

// The API answers errors with HTTP 200 + { success: false }, so axios never
// rejects on them — unwrap explicitly rather than reading `data` off a failure.
const unwrap = (resp) => {
    const body = resp.data || {};
    if (body.success === false) {
        throw new Error(body.message || 'Request failed');
    }
    return body.data;
};

const fmt = n => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const pct = n => `${Math.round((n || 0) * 100)}%`;

const COMPONENT_LABELS = {
    consistency: 'Consistency',
    volume: 'Volume',
    flow: 'Flow',
    hygiene: 'Hygiene',
    breadth: 'Breadth'
};

const COMPONENT_HELP = {
    consistency: 'Share of working days in the period with at least one commit.',
    volume: 'Code churn against the team\'s 90th percentile. Each commit is capped so one bulk drop cannot dominate.',
    flow: 'Commit count plus how healthy the commit sizes are — small, frequent commits score highest.',
    hygiene: 'Commits linked to an issue, less a penalty for reverts.',
    breadth: 'Repositories and files touched.'
};

const StatTile = ({ label, value, sub }) => (
    <div className="ds-card" style={{ padding: 'var(--ds-space-4)', minWidth: '130px', flex: '1 1 130px' }}>
        <div style={{ fontSize: 'var(--ds-text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--ds-tracking-wide)', color: 'var(--ds-ink-muted)' }}>{label}</div>
        <div className="ds-nums" style={{ fontSize: 'var(--ds-text-xl)', fontWeight: 'var(--ds-weight-semibold)' }}>{value}</div>
        {sub && <div style={{ fontSize: 'var(--ds-text-xs)', color: 'var(--ds-ink-muted)' }}>{sub}</div>}
    </div>
);

const ScoreBar = ({ value }) => (
    <div title={`${value}/100`} style={{ background: 'var(--ds-paper-sunk)', borderRadius: 'var(--ds-radius-pill)', height: '6px', width: '100%', minWidth: '48px' }}>
        <div style={{
            width: `${Math.max(2, Math.min(100, value))}%`,
            height: '100%',
            borderRadius: 'var(--ds-radius-pill)',
            background: value >= 70 ? 'var(--ds-accent)' : (value >= 40 ? 'var(--ds-warn, #c98a2b)' : 'var(--ds-ink-muted)')
        }} />
    </div>
);

const Churn = ({ additions, deletions }) => (
    <span className="ds-nums">
        <span className="ds-tone-good">+{fmt(additions)}</span>
        {' '}
        <span className="ds-tone-bad">−{fmt(deletions)}</span>
    </span>
);

/** Team leaderboard with the score broken into its components. */
const TeamTable = ({ summary, onPick, selected }) => {
    if (!summary || !summary.users.length) {
        return <div className="ds-panel__empty">No commits in this range.</div>;
    }
    return (
        <div className="ds-table-wrap">
            <table className="ds-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Contributor</th>
                        <th>Score</th>
                        {Object.keys(COMPONENT_LABELS).map(key => (
                            <th key={key} title={COMPONENT_HELP[key]}>{COMPONENT_LABELS[key]}</th>
                        ))}
                        <th>Commits</th>
                        <th>Code</th>
                        <th>Active days</th>
                        <th>Repos</th>
                        <th title="Median lines changed per commit">Median size</th>
                        <th title="Commits referencing an issue">Linked</th>
                    </tr>
                </thead>
                <tbody>
                    {summary.users.map(u => (
                        <tr key={u.username || u.name}
                            onClick={() => u.username && onPick(u.username)}
                            className={classNames({ 'is-active': selected === u.username })}
                            style={{ cursor: u.username ? 'pointer' : 'default' }}>
                            <td className="ds-nums">{u.rank}</td>
                            <td>
                                {u.username || <span className="ds-tone-warn">{u.name} (unmapped)</span>}
                            </td>
                            <td className="ds-nums" style={{ fontWeight: 'var(--ds-weight-semibold)' }}>{u.score}</td>
                            {Object.keys(COMPONENT_LABELS).map(key => (
                                <td key={key} style={{ minWidth: '64px' }}>
                                    <ScoreBar value={u.components[key]} />
                                </td>
                            ))}
                            <td className="ds-nums">{fmt(u.commits)}</td>
                            <td><Churn additions={u.additions} deletions={u.deletions} /></td>
                            <td className="ds-nums">{u.activeDays}/{u.periodWorkingDays}</td>
                            <td className="ds-nums">{u.projects}</td>
                            <td className="ds-nums">{fmt(u.medianCommitSize)}</td>
                            <td className="ds-nums">{pct(u.issueLinkRate)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Commits vs tracked dev hours — the days that need a conversation. */
const DevHoursPanel = ({ devHours }) => {
    if (!devHours || !devHours.days.length) {
        return null;
    }
    const flagged = devHours.days.filter(d => d.flag);
    return (
        <div className="ds-panel" style={{ marginBottom: 'var(--ds-space-5)' }}>
            <div className="ds-panel__head">
                <div className="ds-panel__title">Commits vs tracked dev hours</div>
                <div className="ds-panel__sub">
                    {devHours.totalDevHours}h tracked · {devHours.totalCommits} commits ·{' '}
                    {devHours.commitsPerDevHour === null ? '—' : `${devHours.commitsPerDevHour} commits/hour`}
                </div>
            </div>
            <div className="ds-table-wrap">
                <table className="ds-table">
                    <thead>
                        <tr><th>Day</th><th>Dev hours</th><th>Commits</th><th>Lines changed</th><th>Lines / dev hour</th><th>Flag</th></tr>
                    </thead>
                    <tbody>
                        {(flagged.length ? flagged : devHours.days).map(d => (
                            <tr key={d.day}>
                                <td className="ds-nums">{d.day}</td>
                                <td className="ds-nums">{d.devHours}</td>
                                <td className="ds-nums">{d.commits}</td>
                                <td className="ds-nums">{fmt(d.churn)}</td>
                                <td className="ds-nums">{d.churnPerDevHour === null ? '—' : fmt(d.churnPerDevHour)}</td>
                                <td>
                                    {d.flag === 'dev_time_no_commits' && <span className="ds-badge ds-tone-warn">dev time, no commits</span>}
                                    {d.flag === 'commits_no_dev_time' && <span className="ds-badge ds-tone-mute">commits, no tracked time</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {flagged.length > 0 && <div className="ds-panel__sub" style={{ padding: 'var(--ds-space-3)' }}>
                Showing {flagged.length} flagged day(s) of {devHours.days.length}.
            </div>}
        </div>
    );
};

const CommitTable = ({ result, onPage }) => {
    if (!result || !result.rows.length) {
        return <div className="ds-panel__empty">No commits match these filters.</div>;
    }
    const pages = Math.ceil(result.total / result.limit);
    return (
        <div className="ds-panel">
            <div className="ds-panel__head">
                <div className="ds-panel__title">Commits</div>
                <div className="ds-panel__sub">{fmt(result.total)} total</div>
            </div>
            <div className="ds-table-wrap">
                <table className="ds-table">
                    <thead>
                        <tr>
                            <th>Date</th><th>Author</th><th>Project</th><th>Branch</th>
                            <th>Message</th><th>Code</th><th>Files</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.rows.map(c => (
                            <tr key={c.id}>
                                <td className="ds-nums">{moment(c.committedAt).format('YYYY-MM-DD HH:mm')}</td>
                                <td>{c.username || <span className="ds-tone-warn" title={c.authorEmail}>{c.authorName}</span>}</td>
                                <td title={c.projectPath}>{c.projectName}</td>
                                <td>{c.branch}{c.isDefaultBranch && <span className="ds-badge" style={{ marginLeft: 4 }}>default</span>}</td>
                                <td>
                                    <a href={c.webUrl} target="_blank" rel="noreferrer">{c.title}</a>
                                    {c.issueRef && <span className="ds-badge" style={{ marginLeft: 4 }}>{c.issueRef}</span>}
                                </td>
                                <td><Churn additions={c.codeAdditions} deletions={c.codeDeletions} /></td>
                                <td className="ds-nums">{fmt(c.codeFiles)}</td>
                                <td>
                                    {c.excluded && <span className="ds-badge ds-tone-mute" title={`Excluded: ${c.excludeReason}`}>{c.excludeReason}</span>}
                                    {!c.excluded && c.noiseRatio > 0.2 && <span className="ds-badge ds-tone-warn" title="Part of this commit is vendored or generated">{pct(c.noiseRatio)} noise</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {pages > 1 && <div className="ds-panel__toolbar" style={{ gap: 'var(--ds-space-2)' }}>
                <button className="ds-btn" disabled={result.page <= 1} onClick={() => onPage(result.page - 1)}>Prev</button>
                <span className="ds-nums">Page {result.page} / {pages}</span>
                <button className="ds-btn" disabled={result.page >= pages} onClick={() => onPage(result.page + 1)}>Next</button>
            </div>}
        </div>
    );
};

export const ProjectContrib = (props) => {
    const today = moment().format('YYYY-MM-DD');
    const monthAgo = moment().subtract(30, 'days').format('YYYY-MM-DD');

    const [from, setFrom] = useState(monthAgo);
    const [to, setTo] = useState(today);
    const [username, setUsername] = useState(props.username || '');
    const [projectFilter, setProjectFilter] = useState('');
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [branch, setBranch] = useState('');
    const [search, setSearch] = useState('');
    const [includeExcluded, setIncludeExcluded] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const [projects, setProjects] = useState([]);
    const [summary, setSummary] = useState(null);
    const [calendar, setCalendar] = useState([]);
    const [commits, setCommits] = useState(null);
    const [devHours, setDevHours] = useState(null);
    const [status, setStatus] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');
    const pollRef = useRef(null);

    // A sync runs in the background on the server, so stop polling if the page
    // goes away mid-run rather than leaving a timer updating a dead component.
    useEffect(() => () => clearTimeout(pollRef.current), []);

    const acRef = useRef(null);

    useEffect(() => {
        if (!showDropdown) {
            return undefined;
        }
        const onDown = (e) => {
            if (acRef.current && !acRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [showDropdown]);

    const params = useCallback((extra = {}) => ({
        from,
        to,
        username: username || undefined,
        projectIds: selectedProjects.length ? selectedProjects.join(',') : undefined,
        branch: branch || undefined,
        search: search || undefined,
        includeExcluded: includeExcluded ? 'true' : undefined,
        ...extra
    }), [from, to, username, selectedProjects, branch, search, includeExcluded]);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        const calls = [
            iAxios.get(`${API}/summary`, { params: params() }),
            iAxios.get(`${API}/calendar`, { params: params() }),
            iAxios.get(`${API}/projects`, { params: params() }),
            iAxios.get(`${API}/commits`, { params: params({ page, limit: PAGE_SIZE }) }),
            iAxios.get(`${API}/status`)
        ];
        if (username) {
            calls.push(iAxios.get(`${API}/user/${username}`, { params: params() }));
        }
        Promise.all(calls).then((resps) => {
            setSummary(unwrap(resps[0]));
            setCalendar(unwrap(resps[1]));
            setProjects(unwrap(resps[2]));
            setCommits(unwrap(resps[3]));
            setStatus(unwrap(resps[4]));
            setDevHours(resps[5] ? unwrap(resps[5]).devHours : null);
            setLoading(false);
        }).catch((ex) => {
            console.error(ex);
            setError(ex.response?.data?.message || ex.message || 'Failed to load contributions');
            setLoading(false);
        });
    }, [params, page, username]);

    useEffect(() => { load(); }, [load]);

    const dbSearch = debounce(setSearch, 500, { maxWait: 700, leading: false, trailing: true });
    const dbBranch = debounce(setBranch, 500, { maxWait: 700, leading: false, trailing: true });

    const resetPage = fn => (...args) => { setPage(1); fn(...args); };

    // Kick off a background ingest, then poll /status until the server reports
    // it finished and reload the page data.
    const handleSync = () => {
        setSyncing(true);
        setSyncMsg('Starting sync…');
        iAxios.post(`${API}/sync`, {
            projectIds: selectedProjects.length ? selectedProjects : undefined
        }).then((resp) => {
            unwrap(resp);
            const poll = () => {
                iAxios.get(`${API}/status`).then((s) => {
                    const st = unwrap(s);
                    const run = st.sync || {};
                    if (run.running) {
                        setSyncMsg(`Syncing… ${run.projects} repo(s) scanned, ${fmt(run.added)} new commit(s)`);
                        pollRef.current = setTimeout(poll, 3000);
                        return;
                    }
                    setSyncing(false);
                    setSyncMsg(run.error
                        ? `Sync failed: ${run.error}`
                        : `Sync finished — ${fmt(run.added)} new commit(s) across ${run.projects} repo(s).`);
                    load();
                }).catch(() => {
                    setSyncing(false);
                    setSyncMsg('Sync started, but progress could not be read. Refresh in a moment.');
                });
            };
            pollRef.current = setTimeout(poll, 1500);
        }).catch((ex) => {
            setSyncing(false);
            setSyncMsg(ex.message || 'Could not start sync');
        });
    };

    const toggleProject = (e) => {
        const next = selectedProjects.filter(id => id !== e.target.name);
        if (e.target.checked) {
            next.push(e.target.name);
        }
        setPage(1);
        setSelectedProjects(next);
    };

    // LinearMonth wants entries keyed by month; the day-rollup rows carry their
    // own `count` so the badge shows commits, not row count.
    const months = calendar.reduce((agg, row) => {
        const key = moment(row.day).startOf('month').format('YYYY-MM-01');
        if (!agg[key]) {
            agg[key] = [];
        }
        agg[key].push({
            id: `day_${row.day}`,
            date: row.day,
            count: row.commits,
            title: `${row.commits} commit(s) · +${row.additions} −${row.deletions}`
        });
        return agg;
    }, {});

    const totals = summary ? summary.totals : null;

    return <div>
        {!props.embedded && <div className="page-head"><h2>Contributions</h2></div>}

        <form className="ds-field-row" onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}>
            <input className="ds-input" style={{ maxWidth: '150px' }} type="date" value={from}
                onChange={resetPage(e => setFrom(e.target.value))} />
            <input className="ds-input" style={{ maxWidth: '150px' }} type="date" value={to}
                onChange={resetPage(e => setTo(e.target.value))} />

            <div className="ds-field" style={{ position: 'relative' }} ref={acRef}>
                <input className="ds-input"
                    onFocus={() => setShowDropdown(true)}
                    placeholder={selectedProjects.length ? `${selectedProjects.length} project(s)` : 'All projects'}
                    onChange={e => setProjectFilter(e.target.value)} />
                <div onClick={e => e.stopPropagation()}
                    className={classNames('ds-dropdown__panel', { 'is-open': showDropdown })}
                    style={{ left: 0, right: 'auto', minWidth: '320px', maxHeight: '320px', overflowY: 'auto' }}>
                    {projects
                        .filter(p => `${p.name} ${p.path}`.toLowerCase().indexOf(projectFilter.toLowerCase()) >= 0)
                        .map(p => (
                            <label key={p.projectId} className="ds-dropdown__item" style={{ justifyContent: 'flex-start', gap: 'var(--ds-space-2)' }}>
                                <input name={p.projectId} type="checkbox" className="ds-tree__check"
                                    checked={selectedProjects.includes(p.projectId)} onChange={toggleProject} />
                                <span>{p.name}</span>
                                <span className="ds-tree__count" style={{ marginLeft: 'auto' }}>
                                    {p.synced
                                        ? p.commits
                                        : <span className="ds-tone-mute" title="No commits indexed for this range — select it and press Sync">not synced</span>}
                                </span>
                            </label>
                        ))}
                    {projects.length === 0 && <div className="ds-dropdown__item">No repos found — check the GitLab sources under Admin</div>}
                </div>
            </div>

            <input className="ds-input" style={{ maxWidth: '150px' }} placeholder="Branch"
                defaultValue={branch} onChange={e => { setPage(1); dbBranch(e.target.value); }} />
            <input className="ds-input" style={{ maxWidth: '180px' }} placeholder="Search message"
                onChange={e => { setPage(1); dbSearch(e.target.value); }} />
            <label className="ds-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--ds-space-1)' }}>
                <input type="checkbox" checked={includeExcluded}
                    onChange={resetPage(e => setIncludeExcluded(e.target.checked))} />
                Show vendor / merge commits
            </label>
            <button className="ds-btn is-primary" type="submit">Refresh</button>
            <button className="ds-btn" type="button" onClick={handleSync} disabled={syncing}
                title={selectedProjects.length
                    ? `Pull commits for ${selectedProjects.length} selected repo(s)`
                    : 'Pull commits for every repo'}>
                {syncing ? 'Syncing…' : (selectedProjects.length ? `Sync ${selectedProjects.length} repo(s)` : 'Sync all repos')}
            </button>
        </form>

        {syncMsg && <div className="ds-panel__sub" style={{ marginBottom: 'var(--ds-space-4)' }}>{syncMsg}</div>}

        {username && <div className="tag-cloud" style={{ marginBottom: 'var(--ds-space-4)' }}>
            <label className="tag-chip is-active">
                {username}
                <button className="ds-btn" style={{ marginLeft: 'var(--ds-space-2)' }}
                    onClick={() => { setPage(1); setUsername(''); }}>clear</button>
            </label>
        </div>}

        {error && <div className="ds-panel ds-tone-bad" style={{ padding: 'var(--ds-space-4)', marginBottom: 'var(--ds-space-4)' }}>{error}</div>}

        {totals && <div style={{ display: 'flex', gap: 'var(--ds-space-3)', flexWrap: 'wrap', marginBottom: 'var(--ds-space-5)' }}>
            <StatTile label="Commits" value={fmt(totals.commits)} sub="excluding merges and vendored churn" />
            <StatTile label="Lines added" value={fmt(totals.additions)} />
            <StatTile label="Lines removed" value={fmt(totals.deletions)} />
            <StatTile label="Files touched" value={fmt(totals.files)} />
            <StatTile label="Contributors" value={fmt(totals.contributors)} />
        </div>}

        <div className="ds-panel" style={{ padding: 'var(--ds-space-3)', marginBottom: 'var(--ds-space-5)' }}>
            <div className="scroll-x"><LinearMonth months={months} /></div>
        </div>

        {devHours && <DevHoursPanel devHours={devHours} />}

        <div className="ds-panel" style={{ marginBottom: 'var(--ds-space-5)' }}>
            <div className="ds-panel__head">
                <div className="ds-panel__title">Contribution profile</div>
                <div className="ds-panel__sub">
                    Scored within this period and team — read the components, not just the total.
                    Each commit's churn is capped at {summary ? summary.churnCap : 400} lines.
                </div>
            </div>
            <TeamTable summary={summary} selected={username}
                onPick={(name) => { setPage(1); setUsername(name === username ? '' : name); }} />
        </div>

        <CommitTable result={commits} onPage={setPage} />

        {status && <div className="ds-panel__sub" style={{ marginTop: 'var(--ds-space-4)' }}>
            {fmt(status.total)} commits indexed · newest {status.newestCommitAt ? moment(status.newestCommitAt).fromNow() : '—'}
            {status.pendingEnrichment > 0 && ` · ${fmt(status.pendingEnrichment)} awaiting file-level analysis`}
        </div>}

        {loading && <Spinner />}
    </div>;
};
